/**
 * This is a wrapper around react-intl's IntlProvider that adds the ability to
 * change the locale at runtime. The idea here is that we have the provider
 * at the top of the render tree (above the login provider, etc.) so that
 * we can internationalize the login page. Then, once the user is logged in,
 * we update the locale to match the user's preferred locale (or leave it
 * as the default locale if the user hasn't set a preferred locale).
 */
import {
  MessageFormatElement,
  parse as parseICUFormat,
  TYPE as ICU_TYPE,
} from "@formatjs/icu-messageformat-parser";
import { printAST } from "@formatjs/icu-messageformat-parser/printer";
import mapValues from "lodash-es/mapValues";
import React from "react";
import { ReactElement, ReactNode } from "react";
import {
  IntlProvider as ReactIntlProvider,
  // eslint-disable-next-line no-restricted-imports
  useIntl as useReactIntl,
  IntlShape,
} from "react-intl";

import { SupportedLanguage } from "~frontend/__generated__/graphql";
import enUS from "~frontend/locales/en-us.json";
import es from "~frontend/locales/es.json";

function generatePlaceholder(icuMessageString: string): string {
  const parsed = parseICUFormat(icuMessageString);

  function visit(node: MessageFormatElement) {
    switch (node.type) {
      case ICU_TYPE.literal:
        node.value = node.value.replace(/[a-zA-Z]/g, "X");
        break;
      case ICU_TYPE.tag:
        node.children.forEach(visit);
        break;
      case ICU_TYPE.plural:
        Object.entries(node.options).forEach(([_, value]) => {
          value.value.forEach(visit);
        });
        break;
      case ICU_TYPE.select:
        Object.entries(node.options).forEach(([_, value]) => {
          value.value.forEach(visit);
        });
        break;
    }
  }

  parsed.forEach(visit);
  return printAST(parsed);
}

const messages: Record<string, any> = {
  "en-us": mapValues(enUS, "translation"),
  xx: mapValues(enUS, val => generatePlaceholder(val.translation)),
  es,
};

export const DEFAULT_LANGUAGE = SupportedLanguage.EnUs;

export function detectBrowserLanguage(): SupportedLanguage {
  const navigatorLanguages = navigator.languages || [navigator.language];
  for (const language of navigatorLanguages) {
    if (language.toLowerCase().startsWith("en")) {
      return SupportedLanguage.EnUs;
    }

    if (language.startsWith("es")) {
      return SupportedLanguage.Es;
    }
  }

  return DEFAULT_LANGUAGE;
}

export type IntlContextValue = {
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage | null) => void;
} & IntlShape;

const IntlContext = React.createContext<IntlContextValue | null>(null);

export function useIntl(): IntlContextValue {
  const contextValue = React.useContext(IntlContext);
  if (!contextValue) {
    throw new Error("useIntl must be used within an IntlProvider");
  }

  return contextValue;
}

function IntlProviderInner({
  children,
  language,
  setLanguage,
}: {
  children: ReactNode;
  language: SupportedLanguage;
  setLanguage: (language: SupportedLanguage) => void;
}): ReactElement {
  const intl = useReactIntl();
  const contextValue = React.useMemo(
    () => ({
      ...intl,
      language,
      setLanguage: (maybeLanguage: SupportedLanguage | null) => {
        setLanguage(maybeLanguage || detectBrowserLanguage());
      },
    }),
    [intl, language, setLanguage]
  );

  return (
    <IntlContext.Provider value={contextValue}>{children}</IntlContext.Provider>
  );
}

const gqlLanguageToIntlLocale: Record<SupportedLanguage, string> = {
  [SupportedLanguage.EnUs]: "en-us",
  [SupportedLanguage.Es]: "es",
  [SupportedLanguage.Xx]: "xx",
};

export default function IntlProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const [language, setLanguage] = React.useState<SupportedLanguage>(
    detectBrowserLanguage()
  );

  const locale = gqlLanguageToIntlLocale[language];

  return (
    <ReactIntlProvider
      locale={locale}
      defaultLocale="en-us"
      key={locale}
      messages={messages[locale]}
    >
      <IntlProviderInner language={language} setLanguage={setLanguage}>
        {children}
      </IntlProviderInner>
    </ReactIntlProvider>
  );
}
